import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  TrendingUp,
  TrendingDown,
  Api,
  AccountBalanceWallet,
  Assessment,
  CheckCircle,
} from "@mui/icons-material";

const StyledCard = styled(Card)(() => ({
  borderRadius: "16px",
  boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
  border: "1px solid #f1f5f9",
  transition: "all 0.2s ease-in-out",
  height: "100%",
  "&:hover": {
    boxShadow:
      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    transform: "translateY(-2px)",
  },
}));

const formatNumber = (num = 0) => {
  if (!num) return 0;
  if (num >= 1_000_000)
    return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "k";
  return num;
};

const MetricCard = ({
  title,
  value = 0,
  change,
  changeType,
  icon,
  color,
  subtitle,
}) => {
  const isPositive = changeType === "positive";

  return (
    <StyledCard>
      <CardContent
        sx={{
          p: "12px",
          height: "160px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Typography
              variant="body2"
              sx={{
                color: "#424242",
                fontSize: "1.5rem",
                fontWeight: 700,
                mb: 1,
              }}
            >
              {title}
            </Typography>

            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "#1e293b",
                mb: 0.5,
                fontSize: "26px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {value ?? 0}
            </Typography>

            {subtitle && (
              <Typography
                variant="caption"
                sx={{
                  color: "#424242",
                  fontSize: "1.2rem",
                  fontWeight: 400,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>

          <Avatar
            sx={{
              bgcolor: `${color}15`,
              color: color,
              width: 48,
              height: 48,
              "& .MuiSvgIcon-root": {
                fontSize: "2.25rem",
              },
            }}
          >
            {icon}
          </Avatar>
        </Stack>

        {typeof change !== "undefined" && (
          <Stack direction="row" alignItems="center" sx={{ mt: "auto", mb: 0 }}>
            {isPositive ? (
              <TrendingUp sx={{ fontSize: 16, color: "#10b981", mr: 0.5 }} />
            ) : (
              <TrendingDown sx={{ fontSize: 16, color: "#ef4444", mr: 0.5 }} />
            )}

            <Typography
              variant="caption"
              sx={{
                color: isPositive ? "#10b981" : "#ef4444",
                fontWeight: 600,
                fontSize: "12px",
              }}
            >
              {change}
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color: "#64748b",
                ml: 0.5,
                fontSize: "12px",
              }}
            >
              vs last month
            </Typography>
          </Stack>
        )}
      </CardContent>
    </StyledCard>
  );
};

export const DashboardCards = ({ dashboardSummaryData = {} }) => {
  const totalApiRequests = dashboardSummaryData?.totalApiRequests || {};
  const successRate = dashboardSummaryData?.successRate || {};
  const activeApis = dashboardSummaryData?.activeApis || {};
  const creditBalance = dashboardSummaryData?.creditBalance || {};

  return (
    <Box>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Total API Requests */}
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <MetricCard
            title="Total API Requests"
            value={totalApiRequests?.current ?? 0}
            change={
              typeof totalApiRequests?.changePercent === "number"
                ? `${totalApiRequests.changePercent > 0 ? "+" : ""}${
                    totalApiRequests.changePercent
                  }%`
                : undefined
            }
            changeType={
              totalApiRequests?.changePercent >= 0 ? "positive" : "negative"
            }
            icon={<Api />}
            color="#6366f1"
            subtitle="This month"
          />
        </Grid>

        {/* Success Rate */}
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <MetricCard
            title="Success Rate"
            value={`${successRate?.current ?? 0}%`}
            change={
              typeof successRate?.changePercent === "number"
                ? `${successRate.changePercent > 0 ? "+" : ""}${
                    successRate.changePercent
                  }%`
                : undefined
            }
            changeType={
              successRate?.changePercent >= 0 ? "positive" : "negative"
            }
            icon={<CheckCircle />}
            color="#10b981"
            subtitle="Average uptime"
          />
        </Grid>

        {/* Active APIs */}
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <MetricCard
            title="Active APIs"
            value={activeApis?.total ?? 0}
            icon={<Assessment />}
            color="#f59e0b"
            subtitle="Currently running"
          />
        </Grid>

        {/* Credit Balance */}
        <Grid item xs={12} sm={6} md={6} lg={3}>
          <MetricCard
            title="Credit Balance"
            value={`${formatNumber(
              creditBalance?.remainingCredits ?? 0
            )} / ${formatNumber(creditBalance?.totalCredits ?? 0)}`}
            change={
              typeof creditBalance?.usageChangePercent === "number"
                ? `${creditBalance.usageChangePercent > 0 ? "+" : ""}${
                    creditBalance.usageChangePercent
                  }%`
                : undefined
            }
            changeType={
              creditBalance?.usageChangePercent >= 0 ? "positive" : "negative"
            }
            icon={<AccountBalanceWallet />}
            color="#8b5cf6"
            subtitle="Credits remaining"
          />
        </Grid>
      </Grid>
    </Box>
  );
};
